// RateLimitSettingsConfiguration.js

import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const SETTING_NAME = 'rate_limiting';

const DEFAULT_SETTINGS = {
  login_max_attempts_per_email: 5,
  login_max_attempts_per_ip: 20,
  login_window_minutes: 15,
  reset_max_attempts_per_email: 3,
  reset_max_attempts_per_ip: 10,
  reset_window_minutes: 15,
  certificate_verify_max_attempts_per_ip: 30,
  certificate_verify_window_minutes: 10
};

function RateLimitSettingsConfiguration() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/settings/get_setting.php?name=${SETTING_NAME}`, { credentials: 'include' })
      .then(response => response.json())
      .then(data => {
        if (data && data.content) {
          setSettings(prev => ({ ...prev, ...data.content }));
        }
      })
      .catch(error => console.error('Error fetching rate limit settings:', error))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    // Every field here is a plain positive integer — guard client-side so
    // an admin can't accidentally save something that would lock everyone
    // out (0) or silently disable the limit (negative); the backend also
    // clamps defensively, but this gives immediate feedback instead of a
    // confusing "why isn't this working" later.
    const numericSettings = {};
    for (const key of Object.keys(DEFAULT_SETTINGS)) {
      const value = parseInt(settings[key], 10);
      if (!Number.isFinite(value) || value < 1) {
        Swal.fire({ icon: 'warning', text: 'All fields must be whole numbers of at least 1.' });
        return;
      }
      numericSettings[key] = value;
    }

    fetch(`${API_BASE_URL}/settings/save_setting.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: SETTING_NAME, content: numericSettings }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setSettings(numericSettings);
          Swal.fire({ icon: 'success', text: 'Rate limiting settings saved.' });
        } else {
          Swal.fire({ icon: 'error', text: 'Failed to save settings: ' + data.message });
        }
      })
      .catch(error => {
        console.error('Error:', error);
        Swal.fire({ icon: 'error', text: 'An error occurred: ' + error.message });
      });
  };

  const handleCancel = () => {
    navigate('/configuration');
  };

  if (loading) {
    return <p>Loading...</p>;
  }

  return (
    <div className='m-2'>
      <h2>Rate Limiting Settings</h2>
      <p className="text-muted">
        Controls brute-force protection on Login, Forgot Password, and public Certificate Verification lookups.
        Each action is blocked once its threshold is reached within the given time window.
      </p>
      <Form>
        <h5 className="mt-3">Login</h5>
        <Form.Group controlId="login_max_attempts_per_email">
          <Form.Label>Max failed attempts per email</Form.Label>
          <Form.Control type="number" min="1" name="login_max_attempts_per_email" value={settings.login_max_attempts_per_email} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="login_max_attempts_per_ip" className="mt-2">
          <Form.Label>Max failed attempts per IP address</Form.Label>
          <Form.Control type="number" min="1" name="login_max_attempts_per_ip" value={settings.login_max_attempts_per_ip} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="login_window_minutes" className="mt-2">
          <Form.Label>Time window (minutes)</Form.Label>
          <Form.Control type="number" min="1" name="login_window_minutes" value={settings.login_window_minutes} onChange={handleChange} />
        </Form.Group>

        <h5 className="mt-4">Forgot Password</h5>
        <Form.Group controlId="reset_max_attempts_per_email">
          <Form.Label>Max requests per email</Form.Label>
          <Form.Control type="number" min="1" name="reset_max_attempts_per_email" value={settings.reset_max_attempts_per_email} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="reset_max_attempts_per_ip" className="mt-2">
          <Form.Label>Max requests per IP address</Form.Label>
          <Form.Control type="number" min="1" name="reset_max_attempts_per_ip" value={settings.reset_max_attempts_per_ip} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="reset_window_minutes" className="mt-2">
          <Form.Label>Time window (minutes)</Form.Label>
          <Form.Control type="number" min="1" name="reset_window_minutes" value={settings.reset_window_minutes} onChange={handleChange} />
        </Form.Group>

        <h5 className="mt-4">Certificate Verification</h5>
        <Form.Group controlId="certificate_verify_max_attempts_per_ip">
          <Form.Label>Max lookups per IP address</Form.Label>
          <Form.Control type="number" min="1" name="certificate_verify_max_attempts_per_ip" value={settings.certificate_verify_max_attempts_per_ip} onChange={handleChange} />
        </Form.Group>
        <Form.Group controlId="certificate_verify_window_minutes" className="mt-2">
          <Form.Label>Time window (minutes)</Form.Label>
          <Form.Control type="number" min="1" name="certificate_verify_window_minutes" value={settings.certificate_verify_window_minutes} onChange={handleChange} />
        </Form.Group>
      </Form>
      <div className='mt-3'>
        <Button variant="primary" className="mr-2" onClick={handleSave}>Save</Button>
        <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default RateLimitSettingsConfiguration;
